import React, { useEffect, useLayoutEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
	faSync,
	faCheck,
	faTrash,
	faPlus,
	faTimesCircle,
} from "@fortawesome/free-solid-svg-icons"
import {
	fetchVehModeles,
	postVehiculeConfig,
	postVehiculeConfigReset,
	deleteVehiculeConfig,
	deleteVehiculeConfigReset,
	updateMasterVehiculeDocs,
	postVehiculeSortie,
	sortieVehiculeReset,
} from "../../../store/actions/autreActions"
import { COLORS } from "../../../utilities/Constants"
import { generalStyles as gs } from "../../styles/styles"
import CustomModal from "../../basics/interface/CustomModal"
import FilteredSelectV2 from "../../basics/input/FilteredSelectV2"
import InputV2 from "../../basics/input/InputV2"
import VehiculeDocumentsV2 from "../entite/VehiculeDocumentsV2"

export const VEHICULE_TYPES = [
	{ value: 0, label: "Dépannage" },
	{ value: 1, label: "Location" },
	{ value: 2, label: "Autre" },
]
const VEHICULE_STATUS = [
	{ value: 0, label: "Disponible" },
	{ value: 1, label: "Indisponible" },
]

const togglerStyle = {
	display: "flex",
	alignItems: "center",
	justifyContent: "space-between",
	cursor: "pointer",
	padding: "10px 2px",
	fontSize: 13,
	fontWeight: 500,
	color: COLORS.textMuted,
	userSelect: "none",
	borderTop: `1px solid ${COLORS.border}`,
	marginTop: 6,
}

const todayISO = () => new Date().toISOString().slice(0, 10)

// `depuisFiche` : ouvert par l'œil de la ligne Véhicule d'une fiche
// d'entretien. On n'y corrige que les parties, les détails techniques et les
// documents (Jordan 12.10) : type, marque, modèle, plaques et statut sont en
// lecture seule (le serveur les ignore aussi), et ni sortie du parc ni
// suppression — tout ça reste dans /configuration → Véhicule.
// `enregistrer` : thunk d'enregistrement (la fiche d'entretien passe par son
// accès dédié, la configuration par la flotte).
const VehiculeV2 = ({
	vehicule,
	onClose,
	inclureSortis = false,
	depuisFiche = false,
	enregistrer = postVehiculeConfig,
}) => {
	const dispatch = useDispatch()
	const marques = useSelector((s) => s.autreInfo.marques)
	const modeles = useSelector((s) => s.autreInfo.modeles)
	const postState = useSelector((s) => s.masterReducer.postVehicule)
	const deleteState = useSelector((s) => s.masterReducer.deleteVehicule)
	const sortieState = useSelector((s) => s.masterReducer.sortieVehicule)
	const isCreate = !vehicule.id
	const actionsParc = !depuisFiche
	// Champs d'identité non modifiables depuis la fiche : champ désactivé, même
	// rendu que les champs verrouillés des autres modals V2 (AgendaRdvModal).
	const identiteFigee = depuisFiche
	const styleFige = identiteFigee
		? {
				backgroundColor: COLORS.bg,
				color: COLORS.textMuted,
				cursor: "not-allowed",
			}
		: {}
	const pv = vehicule.propre_vehicule
	const estSorti = !!(pv && pv.sorti_le)

	const [form, setForm] = useState(() => ({ ...vehicule }))
	const [detailsOpen, setDetailsOpen] = useState(false)
	const [docsOpen, setDocsOpen] = useState(false)
	const [confirmDelete, setConfirmDelete] = useState(false)
	// QA Jordan 27.10 (option A) : passé à « Indisponible » alors qu'un
	// dépanneur le tient → le serveur le libère ; on prévient d'abord
	// (bouton « Confirmer ? », convention des modals V2).
	const [confirmLiberation, setConfirmLiberation] = useState(false)
	const [sortieModal, setSortieModal] = useState(false)
	const [sortieLe, setSortieLe] = useState(todayISO())
	const [raisonSortie, setRaisonSortie] = useState("")
	const [partiesOpen, setPartiesOpen] = useState(
		() =>
			(
				(vehicule.propre_vehicule &&
					vehicule.propre_vehicule.parties) ||
				[]
			).length > 0,
	)
	const detailsRef = useRef(null)
	const docsRef = useRef(null)
	const partiesRef = useRef(null)
	const [docsH, setDocsH] = useState(0)
	const [partiesH, setPartiesH] = useState(0)
	useLayoutEffect(() => {
		const el = docsRef.current
		if (!el) return
		const measure = () => setDocsH(docsOpen ? el.scrollHeight : 0)
		measure()
		if (typeof ResizeObserver === "undefined") return
		const ro = new ResizeObserver(measure)
		ro.observe(el)
		return () => ro.disconnect()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [docsOpen])
	useLayoutEffect(() => {
		const el = partiesRef.current
		if (!el) return
		const measure = () => setPartiesH(partiesOpen ? el.scrollHeight : 0)
		measure()
		if (typeof ResizeObserver === "undefined") return
		const ro = new ResizeObserver(measure)
		ro.observe(el)
		return () => ro.disconnect()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [partiesOpen])
	const refMarque = useRef(null)
	const refModele = useRef(null)
	const refPlaques = useRef(null)
	const refStatut = useRef(null)
	const refChassis = useRef(null)
	const refDateMEC = useRef(null)
	const refExpertise = useRef(null)
	const refKms = useRef(null)
	const update = (k, v) => setForm((f) => ({ ...f, [k]: v }))
	const focusNext = (e, ref) => {
		if (e.key === "Enter") {
			e.preventDefault()
			if (ref.current) ref.current.focus()
		}
	}
	const statutEnter = (e) => {
		if (e.key === "Enter") {
			e.preventDefault()
			setDetailsOpen(true)
			setTimeout(
				() => refChassis.current && refChassis.current.focus(),
				0,
			)
		}
	}
	const updateType = (type) =>
		setForm((f) => ({
			...f,
			propre_vehicule: { ...(f.propre_vehicule || {}), type },
		}))

	const parties = (form.propre_vehicule && form.propre_vehicule.parties) || []
	const setParties = (next) =>
		setForm((f) => ({
			...f,
			propre_vehicule: { ...(f.propre_vehicule || {}), parties: next },
		}))
	const addPartie = () => setParties([...parties, { id: null, label: "" }])
	const updatePartie = (index, label) =>
		setParties(parties.map((p, i) => (i === index ? { ...p, label } : p)))
	const removePartie = (index) =>
		setParties(parties.filter((_, i) => i !== index))

	useEffect(() => {
		if (form.marque) {
			const m = marques.find((x) => x.nom === form.marque)
			if (m) dispatch(fetchVehModeles(m.id))
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		if (!postState.saved && !deleteState.deleted && !sortieState.saved)
			return
		const t = setTimeout(() => {
			dispatch(postVehiculeConfigReset())
			dispatch(deleteVehiculeConfigReset())
			dispatch(sortieVehiculeReset())
			onClose()
		}, 800)
		return () => clearTimeout(t)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [postState.saved, deleteState.deleted, sortieState.saved])

	useEffect(
		() => () => {
			dispatch(postVehiculeConfigReset())
			dispatch(deleteVehiculeConfigReset())
			dispatch(sortieVehiculeReset())
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	)

	useEffect(() => {
		if (deleteState.error) setConfirmDelete(false)
	}, [deleteState.error])

	useEffect(() => {
		if (sortieState.error) setSortieModal(false)
	}, [sortieState.error])

	// Partie retirée mais portée par une fiche ou un entretien : le serveur
	// refuse tout l'enregistrement (message affiché en bas du modal) et donne
	// ses ids → on la remet dans la liste, le reste de la saisie est gardé.
	useEffect(() => {
		const ids =
			postState.error &&
			postState.error.fields &&
			postState.error.fields.parties_utilisees
		if (!Array.isArray(ids) || ids.length === 0) return
		const origine =
			(vehicule.propre_vehicule && vehicule.propre_vehicule.parties) || []
		setForm((f) => {
			const actuelles =
				(f.propre_vehicule && f.propre_vehicule.parties) || []
			const aRemettre = origine.filter(
				(p) =>
					ids.includes(p.id) && !actuelles.some((a) => a.id === p.id),
			)
			if (aRemettre.length === 0) return f
			return {
				...f,
				propre_vehicule: {
					...(f.propre_vehicule || {}),
					parties: [...actuelles, ...aRemettre],
				},
			}
		})
		setPartiesOpen(true)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [postState.error])

	const onSelectMarque = (option) => {
		setForm((f) => ({ ...f, marque: option.nom, modele: "" }))
		dispatch(fetchVehModeles(option.id))
	}

	const type = (form.propre_vehicule && form.propre_vehicule.type) ?? 0

	// Marque / modèle requis là où on peut les saisir ; figés depuis la fiche,
	// ils ne doivent pas bloquer l'enregistrement des détails et des parties.
	const invalid =
		!identiteFigee &&
		(!(form.marque || "").trim() || !(form.modele || "").trim())

	const busy =
		postState.saving ||
		postState.saved ||
		deleteState.deleting ||
		deleteState.deleted ||
		sortieState.saving ||
		sortieState.saved

	const liberation =
		!!(pv && pv.occupe_par) &&
		(vehicule.status ?? 0) !== 1 &&
		(form.status ?? 0) === 1
	const save = () => {
		if (liberation && !confirmLiberation) {
			setConfirmLiberation(true)
			return
		}
		enregistrerVehicule()
	}
	const enregistrerVehicule = () =>
		dispatch(
			enregistrer({
				id: vehicule.id,
				plaques: form.plaques || "",
				marque: form.marque || "",
				modele: form.modele || "",
				chassis: form.chassis || "",
				date_mise_en_circu: form.date_mise_en_circu || "",
				expertise_le: form.expertise_le || "",
				kms: form.kms === "" || form.kms == null ? 0 : form.kms,
				status: form.status ?? 0,
				propre_vehicule: {
					type,
					parties: parties.map((p) => ({
						id: p.id ?? null,
						label: p.label,
					})),
				},
			}),
		)

	const remove = () => {
		if (!confirmDelete) {
			setConfirmDelete(true)
			return
		}
		dispatch(deleteVehiculeConfig(vehicule.id))
	}

	const sortir = () =>
		dispatch(
			postVehiculeSortie({
				vehicule_id: vehicule.id,
				sorti_le: sortieLe,
				raison: raisonSortie,
			}),
		)

	const reintegrer = () =>
		dispatch(
			postVehiculeSortie({
				vehicule_id: vehicule.id,
				reintegrer: true,
			}),
		)

	const buttons = []
	if (!isCreate && actionsParc) {
		if (estSorti) {
			buttons.push({
				title: sortieState.saving ? (
					<FontAwesomeIcon icon={faSync} spin />
				) : (
					"Réintégrer au parc"
				),
				styleName: "primary",
				onClick: reintegrer,
				disabled: busy,
			})
		} else {
			buttons.push({
				title: "Sortir du parc",
				styleName: "secondary",
				onClick: () => setSortieModal(true),
				disabled: busy,
			})
		}
		buttons.push({
			title: deleteState.deleting ? (
				<FontAwesomeIcon icon={faSync} spin />
			) : confirmDelete ? (
				"Confirmer ?"
			) : (
				"Supprimer"
			),
			styleName: "danger",
			onClick: remove,
			disabled: busy,
		})
	}
	buttons.push({
		title: "Annuler",
		styleName: "secondary",
		onClick: onClose,
		disabled: busy,
	})
	if (!estSorti) {
		buttons.push({
			title: postState.saving ? (
				<FontAwesomeIcon icon={faSync} spin />
			) : postState.saved ? (
				<FontAwesomeIcon icon={faCheck} />
			) : liberation && confirmLiberation ? (
				"Confirmer ?"
			) : (
				"Enregistrer"
			),
			styleName: "primary",
			onClick: save,
			disabled: busy || invalid,
		})
	}

	const anyError = postState.error || deleteState.error || sortieState.error
	const errorMsg =
		(anyError && anyError.message) || "Une erreur est survenue."

	return (
		<>
			<CustomModal
				title={isCreate ? "Nouveau véhicule" : "Véhicule"}
				onClose={busy ? () => {} : onClose}
				showCloseButton={!busy}
				buttons={buttons}
			>
				{estSorti && (
					<div
						style={{
							padding: "8px 14px",
							backgroundColor: COLORS.warningLight,
							border: `1px solid ${COLORS.warning}`,
							borderRadius: 8,
							fontSize: 13,
							color: COLORS.warning,
							marginBottom: 12,
						}}
					>
						Sorti le {pv.sorti_le.split("-").reverse().join(".")}
						{pv.raison_sortie ? ` — ${pv.raison_sortie}` : ""}
					</div>
				)}
				{liberation && confirmLiberation && (
					<div
						style={{
							padding: "8px 14px",
							backgroundColor: COLORS.warningLight,
							border: `1px solid ${COLORS.warning}`,
							borderRadius: 8,
							fontSize: 13,
							color: COLORS.warning,
							marginBottom: 12,
						}}
					>
						Pris par {pv.occupe_par_nom || "un dépanneur"}{" "}
						aujourd'hui : il sera libéré (le camion ne lui est plus
						attribué).
					</div>
				)}
				<div style={gs.fieldGroup}>
					<div style={gs.fieldRow}>
						<label style={gs.label}>Type</label>
						<select
							style={{ ...gs.input, ...styleFige }}
							value={type}
							onChange={(e) =>
								updateType(parseInt(e.target.value))
							}
							onKeyDown={(e) => focusNext(e, refMarque)}
							disabled={estSorti || identiteFigee}
						>
							{VEHICULE_TYPES.map((t) => (
								<option key={t.value} value={t.value}>
									{t.label}
								</option>
							))}
						</select>
					</div>

					<div style={gs.fieldRow}>
						<label style={gs.label}>
							Marque
							{!identiteFigee && (
								<span style={{ color: COLORS.danger }}> *</span>
							)}
						</label>
						<FilteredSelectV2
							options={marques}
							defaultKey="nom"
							optionKeys="nom"
							value={form.marque || ""}
							placeholder="Marque…"
							inputRef={refMarque}
							nextRef={refModele}
							onChange={(v) => update("marque", v)}
							onSelect={onSelectMarque}
							disabled={identiteFigee}
							inputStyle={styleFige}
						/>
					</div>

					{form.marque ? (
						<div style={gs.fieldRow}>
							<label style={gs.label}>
								Modèle
								{!identiteFigee && (
									<span style={{ color: COLORS.danger }}>
										{" "}
										*
									</span>
								)}
							</label>
							<FilteredSelectV2
								options={modeles}
								defaultKey="nom"
								optionKeys="nom"
								value={form.modele || ""}
								placeholder="Modèle…"
								inputRef={refModele}
								nextRef={refPlaques}
								onChange={(v) => update("modele", v)}
								onSelect={(o) => update("modele", o.nom)}
								disabled={identiteFigee}
								inputStyle={styleFige}
							/>
						</div>
					) : null}

					<div style={gs.fieldRow}>
						<label style={gs.label}>Plaques</label>
						<input
							ref={refPlaques}
							style={{ ...gs.input, ...styleFige }}
							disabled={identiteFigee}
							value={form.plaques || ""}
							onChange={(e) => update("plaques", e.target.value)}
							onKeyDown={(e) => focusNext(e, refStatut)}
						/>
					</div>

					<div style={gs.fieldRow}>
						<label style={gs.label}>Statut</label>
						<select
							ref={refStatut}
							style={{ ...gs.input, ...styleFige }}
							value={form.status ?? 0}
							onChange={(e) =>
								update("status", parseInt(e.target.value))
							}
							onKeyDown={statutEnter}
							disabled={estSorti || identiteFigee}
						>
							{VEHICULE_STATUS.map((s) => (
								<option key={s.value} value={s.value}>
									{s.label}
								</option>
							))}
						</select>
					</div>

					<div
						style={togglerStyle}
						onClick={() => setPartiesOpen((o) => !o)}
					>
						<span>
							Parties
							{parties.length > 0 ? ` (${parties.length})` : ""}
						</span>
						<span
							style={{
								display: "inline-block",
								transform: partiesOpen
									? "rotate(90deg)"
									: "none",
								transition: "transform 0.15s",
							}}
						>
							▸
						</span>
					</div>
					<div
						style={{
							overflow: "hidden",
							maxHeight: partiesH,
							opacity: partiesOpen ? 1 : 0,
							transition:
								"max-height 0.28s ease, opacity 0.2s ease",
						}}
					>
						<div
							ref={partiesRef}
							style={{
								...gs.fieldGroup,
								paddingTop: 4,
								gap: 8,
							}}
						>
							{parties.length === 0 ? (
								<div
									style={{
										fontSize: 13,
										color: COLORS.textMuted,
										padding: "2px 2px 4px",
									}}
								>
									Aucune partie.
								</div>
							) : (
								parties.map((p, i) => (
									<div
										key={`partie_${i}`}
										style={{
											display: "flex",
											alignItems: "center",
											gap: 8,
										}}
									>
										<input
											style={{
												...gs.input,
												flex: 1,
											}}
											value={p.label}
											placeholder="Nom de la partie…"
											onChange={(e) =>
												updatePartie(i, e.target.value)
											}
										/>
										<button
											type="button"
											title="Supprimer"
											style={gs.iconBtn(COLORS.danger)}
											onClick={() => removePartie(i)}
										>
											<FontAwesomeIcon icon={faTrash} />
										</button>
									</div>
								))
							)}
							<button
								type="button"
								style={{
									...gs.btnSecondary,
									alignSelf: "flex-start",
									padding: "7px 14px",
								}}
								onClick={addPartie}
							>
								<FontAwesomeIcon
									icon={faPlus}
									style={{ marginRight: 6 }}
								/>
								Ajouter une partie
							</button>
						</div>
					</div>

					<div
						style={togglerStyle}
						onClick={() => setDetailsOpen((o) => !o)}
					>
						<span>Détails techniques</span>
						<span
							style={{
								display: "inline-block",
								transform: detailsOpen
									? "rotate(90deg)"
									: "none",
								transition: "transform 0.15s",
							}}
						>
							▸
						</span>
					</div>

					<div
						style={{
							overflow: "hidden",
							maxHeight: detailsOpen
								? detailsRef.current
									? detailsRef.current.scrollHeight
									: 400
								: 0,
							opacity: detailsOpen ? 1 : 0,
							transition:
								"max-height 0.28s ease, opacity 0.2s ease",
						}}
					>
						<div
							ref={detailsRef}
							style={{ ...gs.fieldGroup, paddingTop: 4 }}
						>
							<div style={gs.fieldRow}>
								<label style={gs.label}>Châssis</label>
								<input
									ref={refChassis}
									style={gs.input}
									value={form.chassis || ""}
									onChange={(e) =>
										update("chassis", e.target.value)
									}
									onKeyDown={(e) => focusNext(e, refDateMEC)}
								/>
							</div>

							<div style={gs.fieldRow}>
								<label style={gs.label}>
									Mise en circulation
								</label>
								<InputV2
									ref={refDateMEC}
									type="date"
									style={gs.input}
									value={form.date_mise_en_circu || ""}
									onChange={(e) =>
										update(
											"date_mise_en_circu",
											e.target.value,
										)
									}
									nextRef={refExpertise}
								/>
							</div>

							<div style={gs.fieldRow}>
								<label style={gs.label}>Expertise</label>
								<InputV2
									ref={refExpertise}
									type="date"
									style={gs.input}
									value={form.expertise_le || ""}
									onChange={(e) =>
										update("expertise_le", e.target.value)
									}
									nextRef={refKms}
								/>
							</div>

							<div style={gs.fieldRow}>
								<label style={gs.label}>Kilométrage</label>
								<input
									ref={refKms}
									type="number"
									style={gs.input}
									value={form.kms ?? ""}
									onChange={(e) =>
										update(
											"kms",
											e.target.value === ""
												? ""
												: parseInt(e.target.value),
										)
									}
								/>
							</div>
						</div>
					</div>
					<div
						style={togglerStyle}
						onClick={() => setDocsOpen((o) => !o)}
					>
						<span>Documents</span>
						<span
							style={{
								display: "inline-block",
								transform: docsOpen ? "rotate(90deg)" : "none",
								transition: "transform 0.15s",
							}}
						>
							▸
						</span>
					</div>
					<div
						style={{
							overflow: "hidden",
							maxHeight: docsH,
							opacity: docsOpen ? 1 : 0,
							transition:
								"max-height 0.28s ease, opacity 0.2s ease",
						}}
					>
						<div ref={docsRef} style={{ paddingTop: 4 }}>
							<VehiculeDocumentsV2
								vehiculeId={vehicule.id}
								initialFiles={vehicule.files}
								initialCarteGrise={vehicule.carte_grise}
								isNew={isCreate}
								onDocsChange={(docs) =>
									dispatch(
										updateMasterVehiculeDocs(
											vehicule.id,
											docs,
										),
									)
								}
							/>
						</div>
					</div>
					{anyError && (
						<div
							style={{
								marginTop: 10,
								fontSize: 13,
								color: COLORS.danger,
								textAlign: "center",
							}}
						>
							<FontAwesomeIcon icon={faTimesCircle} /> {errorMsg}
						</div>
					)}
				</div>
			</CustomModal>
			{actionsParc && sortieModal && (
				<CustomModal
					title="Sortir du parc"
					onClose={() => !sortieState.saving && setSortieModal(false)}
					showCloseButton={!sortieState.saving}
					buttons={[
						{
							title: "Annuler",
							styleName: "secondary",
							onClick: () => setSortieModal(false),
							disabled: sortieState.saving,
						},
						{
							title: sortieState.saving ? (
								<FontAwesomeIcon icon={faSync} spin />
							) : (
								"Confirmer la sortie"
							),
							styleName: "danger",
							onClick: sortir,
							disabled: sortieState.saving || !sortieLe,
						},
					]}
				>
					<div style={gs.fieldGroup}>
						<div style={gs.fieldRow}>
							<label style={gs.label}>Date de sortie</label>
							<InputV2
								type="date"
								style={gs.input}
								value={sortieLe}
								onChange={(e) => setSortieLe(e.target.value)}
							/>
						</div>
						<div style={gs.fieldRow}>
							<label style={gs.label}>Raison</label>
							<input
								style={gs.input}
								maxLength={100}
								value={raisonSortie}
								onChange={(e) =>
									setRaisonSortie(e.target.value)
								}
								placeholder="Ex: vendu, accidenté…"
							/>
						</div>
					</div>
				</CustomModal>
			)}
		</>
	)
}

export default VehiculeV2
